import { useTranslation } from "react-i18next";
import { Check, Save } from "lucide-react";

import { useSetRatingDraft } from "../../../application/useSetRatingDraft";
import type { BlsState } from "../../../domain/blsChannel";
import { MAX_SET_NOTE_LENGTH, SUD_VALUES, VOC_VALUES, type EmdrSetRecorder } from "../../../domain/emdrSets";

type Props = {
    state: BlsState | null;
    recorder: EmdrSetRecorder;
    setNumber: number | null;
};

function Scale({
    label,
    hint,
    prefix,
    values,
    value,
    onChange,
}: {
    label: string;
    hint: string;
    prefix: "SUD" | "VOC";
    values: readonly number[];
    value: number | null;
    onChange: (value: number | null) => void;
}) {
    return (
        <fieldset className="flex flex-col gap-2">
            <legend className="mb-2 flex items-baseline gap-2">
                <span className="text-sm font-semibold text-room-ink">{label}</span>
                <span className="text-xs text-room-ink-muted">{hint}</span>
            </legend>
            {/* A grid on a phone, so 0–10 sits in two even rows rather than
                wrapping wherever the width happens to end. */}
            <div role="radiogroup" aria-label={label} className={`grid gap-1.5 sm:flex sm:flex-wrap ${values.length > 7 ? "grid-cols-6" : "grid-cols-7"}`}>
                {values.map((option) => (
                    <button
                        key={option}
                        type="button"
                        role="radio"
                        aria-checked={value === option}
                        aria-label={`${prefix} ${option}`}
                        onClick={() => onChange(value === option ? null : option)}
                        className={`flex h-11 w-full items-center justify-center rounded-xl sm:w-11 text-base font-semibold tabular-nums transition-colors ${
                            value === option
                                ? "bg-room-accent text-white"
                                : "bg-room-active text-room-ink hover:bg-room-hover"
                        }`}
                    >
                        {option}
                    </button>
                ))}
            </div>
        </fieldset>
    );
}

/**
 * The ratings of the set just paused, where the therapist's eyes already are.
 * The patient answers aloud; nothing here reaches their screen.
 */
export default function BoardRating({ state, recorder, setNumber }: Props) {
    const { t } = useTranslation();
    const { paused, sud, setSud, voc, setVoc, note, setNote, saved, save, empty } = useSetRatingDraft(state, recorder);

    if (!paused) return null;

    return (
        <section
            aria-labelledby="emdr-rating-title"
            className="flex flex-col gap-4 rounded-3xl border border-room-accent/40 bg-room-raised p-4 sm:p-6"
            data-testid="emdr-set-form"
        >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 id="emdr-rating-title" className="text-base font-semibold text-room-ink">
                    {setNumber === null ? t("emdr.console.rating.titleUnnumbered") : t("emdr.console.rating.title", { number: setNumber })}
                </h2>
                <p className="text-xs text-room-ink-muted">{t("emdr.console.rating.hint")}</p>
            </div>

            {saved ? (
                <p role="status" className="flex items-center gap-2 text-sm text-room-ok">
                    <Check size={16} aria-hidden="true" />
                    {t("emdr.sets.saved")}
                </p>
            ) : (
                <>
                    <Scale label="SUD" hint={t("emdr.console.rating.sudHint")} prefix="SUD" values={SUD_VALUES} value={sud} onChange={setSud} />
                    <Scale label="VOC" hint={t("emdr.console.rating.vocHint")} prefix="VOC" values={VOC_VALUES} value={voc} onChange={setVoc} />
                    <label className="flex flex-col gap-1.5">
                        <span className="text-sm font-semibold text-room-ink">{t("emdr.sets.note")}</span>
                        <textarea
                            value={note}
                            maxLength={MAX_SET_NOTE_LENGTH}
                            rows={2}
                            onChange={(event) => setNote(event.target.value)}
                            className="rounded-xl border border-room-line bg-room-active px-3 py-2 text-sm text-room-ink"
                        />
                        <span className="text-xs text-room-ink-muted">{t("emdr.sets.noteHint")}</span>
                    </label>
                    <div className="flex flex-wrap items-center gap-3">
                        <button
                            type="button"
                            onClick={() => void save()}
                            disabled={empty || recorder.saving}
                            className="room-control room-control-primary disabled:opacity-50"
                        >
                            <Save size={16} aria-hidden="true" />
                            <span>{t("emdr.sets.save")}</span>
                        </button>
                        <span className="text-xs text-room-ink-muted">{t("emdr.sets.autoSaveHint")}</span>
                    </div>
                </>
            )}

            {recorder.error && (
                <p role="alert" className="text-sm text-room-live">
                    {t(`emdr.sets.errors.${recorder.error}`)}
                </p>
            )}
        </section>
    );
}
